import { ColumnsType } from 'antd/lib/table';
import DashboardTable from 'components/ui/composed/dashboard/DashboardTable';
import { useCallback, useMemo, useState } from 'react';
import { TemplateResponse, TemplateSortParameter } from 'store/template/template';
import useTemplateList from './hooks/useTemplateList';
import { isTemplateListSupported } from './isTemplateListSupported';
import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import {
    declarationExternalTypeToCountry,
    externalTypeToTemplateCountry,
} from 'components/ui/composed/template/template-utils';
import { getDeclarationNameLabels } from '../invoice-upload/utils';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { notification } from 'antd';
import { ContainerOutlined, DeleteOutlined } from '@ant-design/icons';
import { archiveTemplate, deleteTemplate, duplicateTemplate, unarchiveTemplate } from '../../store/template/client';
import { H5 } from '../../components/ui/base/typography';
import { MessageTypes } from '../../store/declarations/enums/common/declaration-types';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';

interface Props {
    country: DeclarationCountry;
    internalType: DeclarationInternalType;
    formType: MessageTypes;
    search?: string;
    viewOnly?: boolean;
    onRowClick?: (id: string | undefined) => void;
    withPagination?: boolean;
    options?: {
        visible?: {
            country?: boolean;
            declarationType?: boolean;
            templateName?: boolean;
        };
    };
    atArchivedPath?: boolean;
    type?: 'product-templates';
}

const TemplatesTable = ({
    country,
    internalType,
    formType,
    search,
    viewOnly,
    onRowClick,
    withPagination = true,
    options = { visible: { country: true, declarationType: true, templateName: true } },
    atArchivedPath,
    type,
}: Props) => {
    const isSupportedTemplate = isTemplateListSupported(country, internalType);
    const { templates, refetch: listRequest } = useTemplateList({
        country,
        internalType,
        formType,
        search,
        atArchivedPath,
        withPagination,
        enabled: isSupportedTemplate,
    });
    const { openModal } = useTemplateContext();
    const navigate = useNavigate();

    const [archiveId, setArchiveId] = useState<string | undefined>(undefined);
    const [archiveModalVisible, setArchiveModalVisible] = useState<boolean>(false);
    const showArchiveModal = useCallback(() => setArchiveModalVisible(true), []);
    const hideArchiveModal = useCallback(() => {
        setArchiveModalVisible(false);
        setArchiveId(undefined);
    }, []);
    const [unarchiveModalVisible, setUnarchiveModalVisible] = useState<boolean>(false);
    const showUnarchiveModal = useCallback(() => setUnarchiveModalVisible(true), []);
    const hideUnarchiveModal = useCallback(() => {
        setUnarchiveModalVisible(false);
        setArchiveId(undefined);
    }, []);
    const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

    const toFullFormType = useCallback(
        (formType: string | undefined) => {
            const labels = getDeclarationNameLabels(country as any, internalType as any) as any[];
            return labels.find(({ key }) => key === formType)?.value;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [templates]
    );

    const columns: ColumnsType<TemplateResponse> = useMemo(() => {
        const _columns: ColumnsType<TemplateResponse> = [];
        if (options.visible?.country)
            _columns.push({
                title: 'Country',
                dataIndex: TemplateSortParameter.DECLARATION_EXTERNAL_TYPE,
                key: TemplateSortParameter.DECLARATION_EXTERNAL_TYPE,
                align: 'left',
                render: (text: string, record) => (
                    <span>{declarationExternalTypeToCountry(record.declarationExternalType)}</span>
                ),
                width: 152,
                sorter: true,
            });
        if (options.visible?.declarationType)
            _columns.push({
                title: 'Form Type',
                dataIndex: TemplateSortParameter.DECLARATION_NAME,
                key: TemplateSortParameter.DECLARATION_NAME,
                align: 'left',
                render: (text: string, record) => <span>{toFullFormType(record.declarationName)}</span>,
                width: 202,
                sorter: true,
            });
        if (options.visible?.templateName)
            _columns.push({
                title: 'Template Name',
                dataIndex: TemplateSortParameter.TEMPLATE_NAME,
                key: TemplateSortParameter.TEMPLATE_NAME,
                align: 'left',
                render: (text: string, record) => <span>{record.templateName}</span>,
                sorter: true,
            });
        return _columns;
    }, [options.visible?.country, options.visible?.declarationType, options.visible?.templateName, toFullFormType]);

    const handleOpenModal = (id: string) => {
        const template = templates.list.find((t) => t.id === id);
        const country = externalTypeToTemplateCountry(template?.declarationExternalType);
        let declarationType = template?.declarationInternalType as 'IMPORT' | 'IMPORT_NEW' | 'EXPORT' | 'ENS' | 'NCTS';
        if (declarationType === 'IMPORT_NEW') declarationType = 'IMPORT';

        openModal?.({
            templateId: template?.id,
            country,
            declarationType,
            formType: template?.declarationName as any,
            name: template?.templateName,
            viewOnly: false,
        });
        navigate(`edit/${id}`);
    };

    const handleDuplicate = async (id: string) => {
        const template = await duplicateTemplate(id, country, internalType, formType);
        if (!template) return;
        const newCountry = externalTypeToTemplateCountry(template?.declarationExternalType);
        let declarationType = template?.declarationInternalType as 'IMPORT' | 'IMPORT_NEW' | 'EXPORT' | 'ENS' | 'NCTS';
        if (declarationType === 'IMPORT_NEW') declarationType = 'IMPORT';
        openModal?.({
            templateId: template?.id,
            country: newCountry,
            declarationType,
            formType: template?.declarationName as any,
            name: template?.templateName,
            viewOnly: false,
        });
        navigate(`edit/${id}`);
    };

    // const handleEdit = (id: string) => {
    //     const template = templates.list.find((t) => t.id === id);
    //     openDrawer(template);
    // };

    const handleArchive = (id: string) => {
        setArchiveId(id);
        showArchiveModal();
    };

    const handleUnarchive = (id: string) => {
        setArchiveId(id);
        showUnarchiveModal();
    };

    const handleDelete = (id: string) => {
        setDeleteId(id);
        setDeleteModalVisible(true);
    };

    const archiveTemplateFunc = () => {
        hideArchiveModal();
        if (!archiveId) return notification.error({ message: 'Could not archive the template!', placement: 'top' });
        archiveTemplate(archiveId)
            .then(() => {
                listRequest();
                notification.success({
                    message: 'Template Archived',
                    description: 'The template has been successfully added to the archive.',
                    placement: 'top',
                });
            })
            .catch(() => {
                notification.error({ message: 'Could not archive the template!', placement: 'top' });
            })
            .finally(() => {
                setArchiveId(undefined);
            });
    };
    const unarchiveTemplateFunc = () => {
        hideUnarchiveModal();
        if (!archiveId) return notification.error({ message: 'Could not unarchive the template!', placement: 'top' });
        unarchiveTemplate(archiveId)
            .then(() => {
                listRequest();
                notification.success({ message: 'Template unarchived successfully!', placement: 'top' });
            })
            .catch(() => {
                notification.error({ message: 'Could not unarchive the template!', placement: 'top' });
            })
            .finally(() => {
                setArchiveId(undefined);
            });
    };

    const deleteTemplateFunc = () => {
        setDeleteModalVisible(false);
        if (!deleteId) return notification.error({ message: 'Could not delete the template!', placement: 'top' });
        deleteTemplate(deleteId)
            .then(() => {
                listRequest();
                notification.success({
                    message: 'Template Deleted',
                    description: 'The template has been successfully deleted.',
                    placement: 'top',
                });
            })
            .catch(() => {
                notification.error({ message: 'Could not delete the template!', placement: 'top' });
            })
            .finally(() => {
                setDeleteId(undefined);
            });
    };

    return (
        <>
            <DashboardTable
                columns={columns}
                data={isSupportedTemplate ? templates : undefined}
                withPagination={withPagination}
                onPaginatorChange={listRequest}
                doSelectOnClick={false}
                onRowSelection={viewOnly ? (ids) => onRowClick?.(ids[0]) : undefined}
                rowSelectionType="radio"
                onDuplicate={viewOnly ? undefined : handleDuplicate}
                onEdit={viewOnly ? undefined : (id) => id && navigate(`edit/${id}`)}
                //  TODO: Add delete modal for achived DELETE /declaration-templates/{templateId}
                onDelete={!atArchivedPath ? undefined : (id) => handleDelete(id)}
                onRowClick={(r) => {
                    handleOpenModal(r);
                    navigate(`edit/${r}`);
                }}
                onUnarchive={type === 'product-templates' ? undefined : handleUnarchive}
                onArchive={type === 'product-templates' ? undefined : handleArchive}
                atArchivedPath={atArchivedPath}
            />

            <ConfirmationModal
                title="Archive Template?"
                icon={<ContainerOutlined />}
                open={archiveModalVisible}
                onConfirm={archiveTemplateFunc}
                onCancel={hideArchiveModal}
                width={400}
                confirmButtonText="Archive"
                cancelButtonText="Cancel"
                content={'The template will be added to the archive.'}
            />

            <ConfirmationModal
                title={<H5>Unarchive Template?</H5>}
                icon={<ContainerOutlined />}
                open={unarchiveModalVisible}
                onConfirm={unarchiveTemplateFunc}
                onCancel={hideUnarchiveModal}
                width={400}
                confirmButtonText="Unarchive"
                cancelButtonText="Cancel"
                content={'The template will be unarchived and become active.'}
            />

            <ConfirmationModal
                title={<H5>Delete Template?</H5>}
                icon={<DeleteOutlined />}
                open={deleteModalVisible}
                onConfirm={deleteTemplateFunc}
                onCancel={() => setDeleteModalVisible(false)}
                width={400}
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                content={'The template will be removed.'}
            />
        </>
    );
};

export default TemplatesTable;
